import { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useApiTokenApi } from '@/services/api';
import { t } from '@/i18n';
import type { ApiTokenCreated, ApiTokenStatus } from '@ip/shared-types';

interface ApiTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ConfirmAction = 'rotate' | 'revoke' | null;

export function ApiTokenDialog({ open, onOpenChange }: ApiTokenDialogProps) {
  const { getApiToken, generateApiToken, revokeApiToken } = useApiTokenApi();
  const [status, setStatus] = useState<ApiTokenStatus | null>(null);
  const [newToken, setNewToken] = useState<ApiTokenCreated | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewToken(null);
    setConfirmAction(null);
    setLoading(true);
    getApiToken()
      .then(setStatus)
      .catch(() => {
        setStatus(null);
        toast.error(t('profile.apiToken.loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [open, getApiToken]);

  const handleGenerate = useCallback(async () => {
    setBusy(true);
    try {
      const created = await generateApiToken();
      setNewToken(created);
      setStatus({
        exists: true,
        tokenPrefix: created.tokenPrefix,
        createdAt: created.createdAt,
        lastUsedAt: null,
      });
      setConfirmAction(null);
    } catch {
      toast.error(t('profile.apiToken.generateFailed'));
    } finally {
      setBusy(false);
    }
  }, [generateApiToken]);

  const handleRevoke = useCallback(async () => {
    setBusy(true);
    try {
      await revokeApiToken();
      setStatus({ exists: false });
      setNewToken(null);
      setConfirmAction(null);
      toast.success(t('profile.apiToken.revoked'));
    } catch {
      toast.error(t('profile.apiToken.revokeFailed'));
    } finally {
      setBusy(false);
    }
  }, [revokeApiToken]);

  const handleCopy = useCallback(async () => {
    if (!newToken) return;
    await navigator.clipboard.writeText(newToken.token);
    toast.success(t('profile.apiToken.copied'));
  }, [newToken]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('profile.apiToken.title')}</DialogTitle>
          <DialogDescription>{t('profile.apiToken.description')}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground size-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {newToken && (
              <div className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3">
                <p className="text-sm font-medium">{t('profile.apiToken.newTokenTitle')}</p>
                <div className="flex items-center gap-2">
                  <code className="bg-muted flex-1 overflow-x-auto rounded px-2 py-1.5 font-mono text-xs">
                    {newToken.token}
                  </code>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    <Copy className="size-4" />
                    {t('profile.apiToken.copy')}
                  </Button>
                </div>
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <TriangleAlert className="size-3.5 shrink-0" />
                  {t('profile.apiToken.newTokenWarning')}
                </p>
              </div>
            )}

            {status?.exists ? (
              <div className="text-sm">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                  <span className="text-muted-foreground">{t('profile.apiToken.tokenLabel')}</span>
                  <span className="font-mono">{status.tokenPrefix}…</span>
                  <span className="text-muted-foreground">{t('profile.apiToken.created')}</span>
                  <span>{status.createdAt ? new Date(status.createdAt).toLocaleString() : '—'}</span>
                  <span className="text-muted-foreground">{t('profile.apiToken.lastUsed')}</span>
                  <span>
                    {status.lastUsedAt
                      ? new Date(status.lastUsedAt).toLocaleString()
                      : t('profile.apiToken.neverUsed')}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('profile.apiToken.noToken')}</p>
            )}

            {confirmAction && (
              <div className="border-destructive/50 bg-destructive/10 space-y-2 rounded-md border p-3">
                <p className="text-sm">
                  {t(
                    confirmAction === 'rotate'
                      ? 'profile.apiToken.rotateConfirm'
                      : 'profile.apiToken.revokeConfirm',
                  )}
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setConfirmAction(null)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={busy}
                    onClick={confirmAction === 'rotate' ? handleGenerate : handleRevoke}
                  >
                    {busy && <Loader2 className="size-4 animate-spin" />}
                    {t('common.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {status?.exists ? (
            <>
              <Button
                variant="outline"
                disabled={busy || loading || confirmAction !== null}
                onClick={() => setConfirmAction('revoke')}
              >
                <Trash2 className="size-4" />
                {t('profile.apiToken.revoke')}
              </Button>
              <Button
                disabled={busy || loading || confirmAction !== null}
                onClick={() => setConfirmAction('rotate')}
              >
                <RefreshCw className="size-4" />
                {t('profile.apiToken.rotate')}
              </Button>
            </>
          ) : (
            <Button disabled={busy || loading} onClick={handleGenerate}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('profile.apiToken.generate')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
